import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { cloneDeep, forEach } from 'lodash-es';
import { BehaviorSubject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { RequestBodyCompany } from '../../../core/models';
import { AuthenticationService } from '../../../core/services';
import { CriteriaToRequestService } from '../../../shared/services/criteria-to-request.service';
import { ResultsService } from '../../../shared/services';
import { BaseApiService } from '../../../shared/services/api';
import { ResultPageDto } from 'src/app/shared/dto';

@Injectable({
    providedIn: 'root'
})
export class CompanyService {
    public _requestResearchData = [];
    searchURL = 'service/contactcloud/universal/';
    companyListScrollPosition = 0;
    public isLoader = false;
    public companyLoadedFlag = new BehaviorSubject(false);
    requestBodyCompanies = new RequestBodyCompany();

    public resultPage: ResultPageDto = {
        IsNavigating: false,
        resultData: [],
        verifiedData: [], // For HV and MV data only
        verifiedDataType: null, // For HV and MV data only
        totalHumanVerifiedCount: 0,
        totalPendingVerifiedCount: 0,
        companySidebarData: null,
        contactsSortedBy: '',
        contactsSortedByDirection: ''
    };
    public companyInternational = new BehaviorSubject(0);
    originalCompany: {companyId: string, companyName: string, competitorsIds: string[], from: 'COMPANIES' | 'CONTACTS'};
    isCompanyInternational = 0;
    private originalResultPage: ResultPageDto = null;
    constructor(
        private readonly authenticationService: AuthenticationService,
        private readonly criteriaToRequestService: CriteriaToRequestService,
        private readonly baseApiService: BaseApiService,
        private readonly router: Router,
        private readonly resultsService: ResultsService,
    ) {
        this.companyInternational.subscribe((internationData) => this.isCompanyInternational = internationData);
        this.resultsService.checkIsNavigating$.subscribe(() => {
            this.resultPage.IsNavigating = this.resultPage.resultData.length > 0;
        });
    }

    setNavigationStatusBySearchResults() {
        this.resultPage.IsNavigating = !!this.resultPage.resultData.length;
    }

    setOriginalResultPage(value):void {
        this.originalResultPage = cloneDeep(value);
    }

    clearCompanyTabData(): void {
        this._requestResearchData = [];
        this.companyListScrollPosition = 0;
        this.isLoader = false;
        this.companyLoadedFlag.next(false);
        this.requestBodyCompanies = new RequestBodyCompany();
        this.companyInternational.next(0);
        this.isCompanyInternational = 0;
        this.originalCompany = null;
        this.originalResultPage = null;
        this.resultPage = {
            IsNavigating: false,
            resultData: [],
            verifiedData: [],
            verifiedDataType: null,
            totalHumanVerifiedCount: 0,
            totalPendingVerifiedCount: 0,
            companySidebarData: null,
            contactsSortedBy: '',
            contactsSortedByDirection: ''
        };
    }

    getOriginalResultPage():ResultPageDto {
        return cloneDeep(this.originalResultPage);
    }

    getCompaniesDetails(requestBody: RequestBodyCompany): Observable<any> {
        requestBody['request_type'] = 'COMPANIES';
        return this.baseApiService.post(this.searchURL + 'search', this.criteriaToRequest(requestBody));
    }

    provideFeedback(params): Observable<any> {
        return this.baseApiService.post('service/contactcloud/research/requestResearchContact', params, { showError: true });
    }

    getCompanyIds(requestBody: RequestBodyCompany): Observable<any> {
        return this.baseApiService.post(this.searchURL + 'companyIds', this.criteriaToRequest(requestBody), { showError: true });
    }

    criteriaToRequest(criteria) {
        const request = {};
        if (criteria.request_type) {
            request['request_type'] = criteria.request_type;
        }
        this.criteriaToRequestService.transformCopy(criteria, request, [
            'keyword',
            'company_location_metro',
            'page',
            'sort_by',
            'sort_direction',
            'company_technographic_sub_categories',
            'technographics',
            'company_location_zipcodes_distance',
            'company_technographic_categories',
            'contact_revenue_min_identifier',
            'contact_revenue_max_identifier',
            'si_intent_topic_ids',
            'intent_topic_ids',
            'intent_minimum_score',
            'intent_topic_threshold',
            'specialties',
            'company_relations',
            'owned_contacts_company',
            'owned_contacts_person',
            'department_size',
            'include_primary_industry_only'
        ]);
        this.criteriaToRequestService.transformCopyWithZero(criteria, request, [
          'company_min_revenue', 'company_max_revenue',
          'company_min_size', 'company_max_size',
          'verified'
        ]);
        this.criteriaToRequestService.transformArrayToCSV(criteria, request, ['company_name', 'match_ids']);
        this.criteriaToRequestService.transDefaultValueCountry(criteria, request, 'location_country_codes');
        this.criteriaToRequestService.transformTiersCsv(criteria, request, 'tiers');
        this.criteriaToRequestService.transformStringToArray(criteria, request, ['company_location_zipcodes', 'company_domains', 'company_names', 'company_naics_codes', 'company_sic_codes']);
        this.criteriaToRequestService.transformIfNotNull(criteria, request, ['company_location_hq', 'company_location_branch', 'company_score_min', 'company_score_max', 'icpintel_job_uuid']);
        this.criteriaToRequestService.transformListArray(criteria, request, ['lists', 'suppression_lists']);
        this.criteriaToRequestService.transformArraytoArray(criteria, request, ['company_technographic_products', 'company_technographic_vendors'], 'value');
        this.criteriaToRequestService.transformMapArrayToArray(criteria, request, 'company_ids', 'companyIds');
        this.criteriaToRequestService.transformObjectToArray(criteria, request, 'company_location_states');
        this.criteriaToRequestService.transformRankings(criteria, request, 'rankings');
        this.criteriaToRequestService.transformCopyWithNull(criteria, request, ['is_international']);
        // this.criteriaToRequestService.transformArrayCopy(criteria, request, 'company_names', 'company_domains');

        if ('company_location_zipcodes_distance' in request && request['company_location_zipcodes_distance'] === 0) {
            delete request['company_location_zipcodes_distance'];
        }
        const company_naics_codes = request['company_naics_codes'];
        if ('company_naics_codes' in request && company_naics_codes.length === 0) {
            delete request['company_naics_codes'];
        }

        const company_sic_codes = request['company_sic_codes'];
        if ('company_sic_codes' in request && company_sic_codes.length === 0) {
            delete request['company_sic_codes'];
        }

        const company_location_metro = request['company_location_metro'];
        if ('company_location_metro' in request && company_location_metro.length === 0) {
            delete request['company_location_metro'];
        }
        const company_technographic_sub_categories = request['company_technographic_sub_categories'];
        if (company_technographic_sub_categories) {
            if ('company_technographic_sub_categories' in request && company_technographic_sub_categories.length === 0) {
                delete request['company_technographic_sub_categories'];
            }
        }
        const company_technographic_categories = request['company_technographic_categories'];
        if (company_technographic_categories) {
            if ('company_technographic_categories' in request && company_technographic_categories.length === 0) {
                delete request['company_technographic_categories'];
            }

        }
        const location_country_codes = request['location_country_codes'];
        if ('location_country_codes' in request && (location_country_codes.length === 0)) {
            delete request['location_country_codes'];
        }

        delete request['tiers'];

        return request;
    }

    purenedObjectCompany(obj) {
        const prunedObject = {};
        forEach(obj, function (value, key) {
            if (Array.isArray(value)) {
                if (value.length > 0) {
                    prunedObject[key] = value;
                }
            } else if (value !== undefined && value !== '') {
                if (value !== null) {
                    prunedObject[key] = value;
                }
            } else if (value !== '') {
                prunedObject[key] = value;
            }
        });
        return prunedObject;
    }

    exportAllCompany(ids: string[], requestCompanies: any, icpIntelJobUUID: string = null, showIntentAutomationView:boolean = false): Observable<any> {
        let intentTopicIdsExist;
        this.authenticationService.currentUser$.pipe(take(1)).subscribe((userData) => {
          intentTopicIdsExist =
            userData.user_meta.allowIntentExport === '1' &&
            (requestCompanies.intent_topic_ids || requestCompanies.si_intent_topic_ids);
        });
        const request = {
          company_ids: ids,
          verified: requestCompanies.verified,
          is_international: requestCompanies.is_international,
          ...((intentTopicIdsExist || showIntentAutomationView) && {
            ...(requestCompanies.intent_topic_ids ? { intent_topic_ids: requestCompanies.intent_topic_ids } : {}),
            ...(requestCompanies.si_intent_topic_ids ? { si_intent_topic_ids: requestCompanies.si_intent_topic_ids } : {}),
          }),
          icpintel_job_uuid: icpIntelJobUUID,
          is_intent_automation_from_email: showIntentAutomationView
        };
        return this.baseApiService.post('service/contactcloud/companies/export', request, {}, {
            responseType: 'text',
            observe: 'response'
        });
    }

    exportAllCompanyInboxIntel(ids, mvCompanies, unmatchedCompanies, requestCompanies) {
        const request = {
            company_ids: ids,
            company_machine_ids: mvCompanies,
            company_unmatched_ids: unmatchedCompanies,
            // verified: requestCompanies.verified,
            is_international: requestCompanies.is_international
        };
        return this.baseApiService.post('service/contactcloud/companies/export', request, {}, {
            responseType: 'text',
            observe: 'response'
        });
    }

    requestResearch() {
        return this.baseApiService.get('service/contactcloud/research/requestResearchCompany').subscribe((data: any) => {
            this._requestResearchData = data;
        });
    }

    getRequestResearchData() {
        return this._requestResearchData;
    }

    updateRequestResearchData(companyId, isUnmatched?) {
        if (isUnmatched) {
            const isExistUnmatched = this._requestResearchData['company_unmatched_ids'].some((data) => data === companyId);
            if (!isExistUnmatched) {
                this._requestResearchData['company_unmatched_ids'].push(companyId);
            }
        } else {
            const isExist = this._requestResearchData['company_ids'].some((data) => data === companyId);
            if (!isExist) {
                this._requestResearchData['company_ids'].push(companyId);
            }
        }
    }

    navigateToCompany(company_id) {
        this.router.routeReuseStrategy.shouldReuseRoute = function () { // 4311
            return false;
        };
        if (this.isCompanyInternational === 1) {
            this.router.navigate(['/company', company_id, 'intl'], {
                state: { 'type': 'company' }
            });
        } else if (this.isCompanyInternational === null) {
            this.router.navigate(['/company', company_id, 'global'], {
                state: { 'type': 'company' }
            });
        } else {
            this.router.navigate(['/company', company_id], {
                state: { 'type': 'company' }
            });
        }
    }
}
